import { useEffect, useRef, useState } from "react";
import { ColumnsType } from "antd/es/table";
import { cellWidth } from "../../../core/methods/tableCellWidth";
import { useNavigate } from "react-router";
import { all_routes } from "../../router/all_routes";
import Datatable from "../../../core/common/dataTable/index";
import TimelineModal from "../../../core/common/TimelineModal";

import {
  parseNumber,
  safeLocaleCompare,
} from "../../../core/methods/sorterMethods";
import {
  SearchFilterErrors,
  SearchFilterRef,
  SearchPayload,
} from "../../../core/common/ManagerSingleSearchFilter/types";
import useApi from "../../../core/customhook/useApi";
import SearchFilter from "../../../core/common/ManagerSingleSearchFilter/SearchFilter";
import { formatDate } from "../../../core/common/TabelValue";
import { PaginationType } from "../../../types/common-type";
import Loading from "../../../core/common/custom-loading/Loading";
import webservice_url, {
  apibaseurl,
} from "../../../core/webservice/Webservice_url";
import { Switch } from "antd";
import { getSearchableHeader } from "../../../core/utils/getSearchableHeader";
import Swal from "sweetalert2";
import { exportAntdTableToExcel } from "../../../core/utils/exportExcel";
import { filterByColumnSearch } from "../../admin/utils/filterByColumnSearch";

export interface CaseTableRecord {
  key: string;
  sno: number;
  applicant_name: string;
  case_id: string;
  case_spoc: string;
  employee_id: string;
  case_received_date: string;
  sla_start_date: string;
  due_date: string;
  total_checks: number;
  work_in_progress: number;
  completed: number;
  stop: number;
  insuff_expire: string;
  actions: string;
}

export const actionOptions = [
  { value: "stop_case", label: "Stop Case" },
  { value: "stop_check", label: "Stop Check" },
  { value: "add_check", label: "Add Check" },
  { value: "generate_report", label: "Generate Report" },
];

const initialSearchData: SearchPayload = {
  org_ids: [],
  project_ids: [],
  location_name: [],
  program_ids: [],
  department_ids: [],
};

type ReportVisibility = {
  assisted: boolean;
  nonAssisted: boolean;
};

// type Props = {
//   status_id?: string;
// };

const WorkInProgress = ({ status_id }: { status_id: string }) => {
  const navigate = useNavigate();
  const [itemList, setItemList] = useState<any[]>([]);
  const [timelineData, setTimelineData] = useState<any[]>([]);
  const [timelineLoading, setTimelineLoading] = useState<boolean>(false);
  const [showTimeline, setShowTimeline] = useState<boolean>(false);
  const filterRef = useRef<SearchFilterRef>(null);
  const [searchData, setSearchData] =
    useState<SearchPayload>(initialSearchData);
  const [errors, setErrors] = useState<SearchFilterErrors>({});
  const [inventoryColumnSearch, setInventoryColumnSearch] = useState<
    Record<string, string>
  >({});
  const [pagination, setPagination] = useState<PaginationType>({
    current: 1,
    pageSize: 1000,
    total: 1,
  });
  const [reportVisibility, setReportVisibility] = useState<
    Record<string, ReportVisibility>
  >({});

  const filteredData = filterByColumnSearch(
    Array.isArray(itemList) ? itemList : [],
    inventoryColumnSearch,
    {
      assisted_checks: (r) =>
        `${r.verified_assisted_checks ?? 0} / ${r.assisted_checks ?? 0}`,
      non_assisted_checks: (r) =>
        `${r.verified_non_assisted_checks ?? 0} / ${r.non_assisted_checks ?? 0}`,
    },
  );

  const liveCaseProgress = useApi<any>(
    "/api/v1/agency/manager-live-case-progress",
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );
  const showReportAPI = useApi<any>(webservice_url.upload_assistedReports, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  const getCaseLog = useApi<any>(webservice_url.ViewLogAPI, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  const handleViewLog = async (caseId?: string) => {
    if (!caseId) return;
    setShowTimeline(true);
    setTimelineLoading(true);
    try {
      const res = await getCaseLog.callApi({ case_id: caseId });
      setTimelineData(res?.data ?? []);
    } catch (err) {
      console.error(err);
      setTimelineData([]);
    } finally {
      setTimelineLoading(false);
    }
  };

  /**
   * API call only. No validation, no focus.
   * Used by the useEffect (page load, status change, pagination change)
   * and by handleSearch after validation passes.
   */
  const fetchCases = async () => {
    try {
      const payload = {
        status: status_id,
        ...searchData,
        page: pagination?.current ?? 1,
        limit: pagination?.pageSize ?? 10,
      };

      const res = await liveCaseProgress.callApi(payload);

      if (!res?.success) {
        setItemList([]);
        setReportVisibility({});
        return;
      }

      const rows = res.data?.rows ?? [];

      setItemList(rows);

      // Set Assisted / Non-Assisted switch values from API
      const visibility: Record<string, ReportVisibility> = {};

      rows.forEach((item: any) => {
        const assignmentId = String(item?.assignment_id ?? "");

        if (assignmentId) {
          visibility[assignmentId] = {
            assisted: Boolean(item?.show_assisted_report),
            nonAssisted: Boolean(item?.show_nonassisted_report),
          };
        }
      });

      setReportVisibility(visibility);

      setPagination((prev) => ({
        ...prev,
        total: res?.data?.pagination?.totalRecords || 0,
      }));
    } catch (err) {
      console.error(err);
    }
  };

  /**
   * Validates required filters (Organization, then Account).
   * Returns true if the search may proceed.
   */
  const validateFilters = (): boolean => {
    if (searchData.org_ids.length === 0) {
      setErrors({ org_ids: "Organization is required" });
      filterRef.current?.focusField("org_id");
      return false;
    }

    if (searchData.project_ids.length === 0) {
      setErrors({ project_ids: "Account is required" });
      filterRef.current?.focusField("project_id");
      return false;
    }

    setErrors({});
    return true;
  };

  /** User-triggered search. Used only by the Search button. */
  const handleSearch = () => {
    if (!validateFilters()) return;
    fetchCases();
  };

  useEffect(() => {
    // if (!status_id) return;
    fetchCases(); // automatic load: no validation, no focus
  }, [status_id, pagination?.current, pagination?.pageSize]);

  /** Updates filters and clears errors as soon as a valid selection is made. */
  const handleFilterChange = (data: SearchPayload) => {
    const orgChanged = data.org_ids.join(",") !== searchData.org_ids.join(",");

    setSearchData(data);
    setErrors((prev) => ({
      // cleared as soon as an Organization is selected
      org_ids: data.org_ids.length > 0 ? undefined : prev.org_ids,
      // cleared when an Account is selected, or when Organization changed
      // (Account was reset, so it will be validated again on next Search)
      project_ids:
        data.project_ids.length > 0 || orgChanged
          ? undefined
          : prev.project_ids,
    }));
  };

  const handleClear = () => {
    filterRef.current?.clear();
    setErrors({});
  };

  const handleTableChange = (pgConfig: any) => {
    if (
      pgConfig.current !== pagination.current ||
      pgConfig.pageSize !== pagination.pageSize
    ) {
      setPagination((prev) => ({
        ...prev,
        current: pgConfig.current,
        pageSize: pgConfig.pageSize,
      }));
    }
  };

  const DownloadAssestedReport = useApi<any>("", {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
    responseType: "blob",
  });
  const handleDownloadReport = async (
    assignmentId: any,
    reportType: "assisted" | "non-assisted",
  ) => {
    const assignmentID = assignmentId?.assignment_id;

    try {
      const response: any = await DownloadAssestedReport.callApi(
        undefined,
        `${webservice_url.DownloadAssestedReport}/${assignmentID}/${reportType}-report/download-pdf`,
      );

      const blob = new Blob([response], {
        type: "application/pdf",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `${reportType}_Report_${assignmentID}.pdf`;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.log(`${reportType} Report Download Error:`, error);
    }
  };
  const handleReportVisibility = async (
    record: any,
    type: "assisted" | "non-assisted",
    checked: boolean,
  ) => {
    const assignmentId = record?.assignment_id;

    if (!assignmentId) return;

    const id = String(assignmentId);

    const current = reportVisibility[id] || {
      assisted: false,
      nonAssisted: false,
    };

    const payload = [
      {
        assignment_id: assignmentId,
        show_assisted_report: type === "assisted" ? checked : current.assisted,

        show_nonassisted_report:
          type === "non-assisted" ? checked : current.nonAssisted,
      },
    ];

    try {
      console.log("Show Report Payload:", payload);

      await showReportAPI.callApi(payload);

      setReportVisibility((prev) => ({
        ...prev,
        [id]: {
          assisted: type === "assisted" ? checked : current.assisted,

          nonAssisted: type === "non-assisted" ? checked : current.nonAssisted,
        },
      }));
    } catch (error) {
      console.error("Show Report Update Error:", error);
    }
  };

  const handleDownloadDrugReport = async (doc: any, record: any) => {
    const path = doc?.file_name;
    if (!path) return;

    const fileUrl = /^https?:\/\//i.test(path)
      ? path
      : `${apibaseurl}/uploads/${path}`;

    // extension original file se
    const ext = (doc.original_name || path).split(".").pop();

    // applicant_name + case_id, safe filename
    const baseName = [record?.applicant_name, record?.case_id]
      .filter(Boolean)
      .join("_")
      .trim()
      .replace(/\s+/g, "_")
      .replace(/[^\w\-]/g, "");

    const fileName = baseName
      ? `${baseName}.${ext}`
      : doc.original_name || path;

    try {
      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error("Failed to download report");

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Drug report download failed", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Unable to download the drug report.",
      });
    }
  };
  const columns: ColumnsType<any> = [
    {
      title: "Sr. No.",
      key: "serial_number",
      dataIndex: "serial_number",
     onCell: () => cellWidth(100),
      align: "center",
    },
    {
      title: getSearchableHeader(
        "Applicant Name",
        "applicant_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "applicant_name",
      key: "applicant_name",
      onCell: () => cellWidth(200),
      render: (text, record: any) => (
        <div
          className="wip-link-cell"
          onClick={() => {
            navigate(all_routes.manager_caseDetails, {
              state: {
                case_id: record.case_id,
                assignment_id: record.assignment_id,
              },
            });
          }}
        >
          {text}
        </div>
      ),
      sorter: (a, b) => safeLocaleCompare(a, b, "applicant_name"),
    },
    {
      title: getSearchableHeader(
        "Case Id",
        "case_id",
        setInventoryColumnSearch,
      ),
      dataIndex: "case_id",
      key: "case_id",
      onCell: () => cellWidth(200),
      sorter: (a, b) => safeLocaleCompare(a, b, "case_id"),
    },
    {
      title: getSearchableHeader(
        "Case Spoc",
        "case_spoc",
        setInventoryColumnSearch,
      ),
      dataIndex: "case_spoc",
      key: "case_spoc",
      onCell: () => cellWidth(200),
      sorter: (a, b) => safeLocaleCompare(a, b, "case_spoc"),
    },
    {
      title: getSearchableHeader(
        "Case Status",
        "overall_status",
        setInventoryColumnSearch,
      ),
      dataIndex: "overall_status",
      key: "overall_status",
      onCell: () => cellWidth(200),
      sorter: (a: any, b: any) =>
        (a.overall_status ?? "").localeCompare(b.overall_status ?? ""),
      render: (text: any, record: any) => {
        return (
          <span
            style={{
              backgroundColor: record?.overall_color_value || "#6c757d",
              color: "#fff",
              padding: "5px 10px",
              borderRadius: "4px",
              display: "inline-block",
              fontSize: "12px",
              fontWeight: 500,
              whiteSpace: "nowrap",
            }}
          >
            {text ?? "-"}
          </span>
        );
      },
    },
    {
      title: getSearchableHeader(
        "Project",
        "project_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "project_name",
      key: "project_name",
      onCell: () => cellWidth(200),
      width: "220px",
      sorter: (a: any, b: any) =>
        (a.project_name ?? "").localeCompare(b.project_name ?? ""),
    },
    {
      title: getSearchableHeader(
        "Program",
        "program_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "program_name",
      key: "program_name",
      onCell: () => cellWidth(200),
      width: "220px",
      sorter: (a: any, b: any) =>
        (a.program_name ?? "").localeCompare(b.program_name ?? ""),
    },
    {
      title: getSearchableHeader(
        "Department",
        "department_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "department_name",
      key: "department_name",
      onCell: () => cellWidth(200),
      width: "220px",
      sorter: (a: any, b: any) =>
        (a.department_name ?? "").localeCompare(b.department_name ?? ""),
    },
    {
      title: getSearchableHeader(
        "Candidate Id",
        "hrms_id",
        setInventoryColumnSearch,
      ),
      dataIndex: "hrms_id",
      key: "hrms_id",
      onCell: () => cellWidth(200),
      sorter: (a, b) => safeLocaleCompare(a, b, "hrms_id"),
    },
    {
      title: getSearchableHeader(
        "Case Received Date",
        "case_received_date",
        setInventoryColumnSearch,
      ),
      dataIndex: "case_received_date",
      key: "case_received_date",
      onCell: () => cellWidth(200),
      render: (value: string) => formatDate(value),
    },
    {
      title: getSearchableHeader(
        "SLA Start Date",
        "sla_start_date",
        setInventoryColumnSearch,
      ),
      dataIndex: "sla_start_date",
      key: "sla_start_date",
      onCell: () => cellWidth(200),
      render: (value: string) => formatDate(value),
    },
    {
      title: getSearchableHeader(
        "Due Date",
        "due_date",
        setInventoryColumnSearch,
      ),
      dataIndex: "due_date",
      key: "due_date",
      onCell: () => cellWidth(200),
      render: (value: string) => formatDate(value),
    },

    {
      title: getSearchableHeader(
        "Total Checks",
        "total_checks",
        setInventoryColumnSearch,
      ),
      dataIndex: "total_checks",
      key: "total_checks",
      align: "center",
      onCell: () => cellWidth(200),
      sorter: (a: any, b: any) =>
        parseNumber(a.total_checks) - parseNumber(b.total_checks),
    },
    {
      title: getSearchableHeader(
        "Assisted",
        "assisted_checks",
        setInventoryColumnSearch,
      ),
      dataIndex: "assisted_checks",
      key: "assisted_checks",
      onCell: () => cellWidth(200),
      align: "center",
      sorter: (a, b) => Number(a.assisted_checks) - Number(b.assisted_checks),
      render: (_value: number, record: any) => {
        const verified = Number(record.assisted_verified_checks ?? 0);
        const total = Number(record.assisted_checks ?? 0);
        const isComplete = verified === total;

        return (
          <div>
            <span className="wip-chip wip-chip-progress">
              {verified} / {total}
            </span>

            <div
              className={`mt-1 ${isComplete ? "text-success" : "text-warning"}`}
            >
              {isComplete ? "Complete" : "In Progress"}
            </div>
          </div>
        );
      },
    },
    {
      title: getSearchableHeader(
        "Non-Assisted",
        "non_assisted_checks",
        setInventoryColumnSearch,
      ),
      dataIndex: "non_assisted_checks",
      key: "non_assisted_checks",
      onCell: () => cellWidth(200),
      align: "center",
      sorter: (a, b) =>
        Number(a.non_assisted_checks) - Number(b.non_assisted_checks),
      render: (_value: number, record: any) => {
        const verified = Number(record.non_assisted_verified_checks ?? 0);
        const total = Number(record.non_assisted_checks ?? 0);
        const isComplete = verified === total;

        return (
          <div>
            <span className="wip-chip wip-chip-progress">
              {verified} / {total}
            </span>

            <div
              className={`mt-1 ${isComplete ? "text-success" : "text-warning"}`}
            >
              {isComplete ? "Complete" : "In Progress"}
            </div>
          </div>
        );
      },
    },
    {
      title: getSearchableHeader(
        "Candidate Submited",
        "candidate_submit",
        setInventoryColumnSearch,
      ),
      dataIndex: "candidate_submit",
      key: "candidate_submit",
      onCell: () => cellWidth(200),
      align: "center",
      sorter: (a, b) => a.candidate_submit - b.candidate_submit,
      render: (value: number) => (
        <span className="wip-chip wip-chip-success">{value ?? 0}</span>
      ),
    },
    {
      title: getSearchableHeader(
        "Work In Progress",
        "work_in_progress",
        setInventoryColumnSearch,
      ),
      dataIndex: "work_in_progress",
      key: "work_in_progress",
      align: "center",
      sorter: (a: any, b: any) =>
        parseNumber(a.work_in_progress) - parseNumber(b.work_in_progress),
    },
    {
      title: getSearchableHeader(
        "Completed",
        "completed",
        setInventoryColumnSearch,
      ),
      dataIndex: "completed",
      key: "completed",
      align: "center",
      sorter: (a: any, b: any) =>
        parseNumber(a.completed) - parseNumber(b.completed),
    },
    {
      title: getSearchableHeader("Stop", "stopped", setInventoryColumnSearch),
      dataIndex: "stopped",
      key: "stopped",
      align: "center",
      sorter: (a: any, b: any) =>
        parseNumber(a.stopped) - parseNumber(b.stopped),
    },
    // {
    //   title: "Insuff Expire",
    //   dataIndex: "insufficiency_expired",
    //   key: "insufficiency_expired",
    //   onCell: () => cellWidth(200),
    //   align: "center",
    //   sorter: (a: any, b: any) =>
    //     parseNumber(a.insufficiency_expired) -
    //     parseNumber(b.insufficiency_expired),
    // },
    {
      title: "Show Assisted Report",
      dataIndex: "",
      key: "show_assisted_report",
      onCell: () => cellWidth(200),
      align: "center",
      render: (_: any, record: any) => {
        const assignmentId = String(record?.assignment_id ?? "");

        const isChecked = reportVisibility[assignmentId]?.assisted ?? false;

        return (
          <Switch
            checked={isChecked}
            loading={showReportAPI.loading}
            onChange={(checked) =>
              handleReportVisibility(record, "assisted", checked)
            }
          />
        );
      },
    },
    {
      title: "Show Non-assisted Report",
      dataIndex: "",
      key: "show_nonassisted_report",
      onCell: () => cellWidth(200),
      align: "center",
      render: (_: any, record: any) => {
        const assignmentId = String(record?.assignment_id ?? "");

        const isChecked = reportVisibility[assignmentId]?.nonAssisted ?? false;

        return (
          <Switch
            checked={isChecked}
            loading={showReportAPI.loading}
            onChange={(checked) =>
              handleReportVisibility(record, "non-assisted", checked)
            }
          />
        );
      },
    },
    {
      title: "Assisted Report",
      dataIndex: "",
      onCell: () => cellWidth(200),
      render: (_: any, record: any) => (
        <button
          className="btn btn-sm btn-primary d-flex gap-2"
          onClick={() => handleDownloadReport(record, "assisted")}
          // disabled={loading}
        >
          <i className="bi bi-download"></i>
          <span className="wip-btn-label">Download</span>
        </button>
      ),
    },
    {
      title: "Non-assisted Report",
      dataIndex: "",
      onCell: () => cellWidth(200),
      render: (_: any, record: any) => (
        <button
          className="btn btn-sm btn-primary d-flex gap-2"
          onClick={() => handleDownloadReport(record, "non-assisted")}
          // disabled={loading}
        >
          <i className="bi bi-download"></i>
          <span className="wip-btn-label">Download</span>
        </button>
      ),
    },

    {
      title: getSearchableHeader(
        "Drug Test",
        "drug_test_status",
        setInventoryColumnSearch,
      ),
      dataIndex: "drug_test_status",
      key: "drug_test_status",
      align: "center",
      sorter: (a, b) => a.drug_test_status - b.drug_test_status,
    },
    {
      title: "Drug Report",
      dataIndex: "drug_report",
      key: "drug_report",
      align: "center",
      onCell: () => cellWidth(200),
      sorter: (a, b) =>
        (a.drug_report?.length ?? 0) - (b.drug_report?.length ?? 0),
      render: (docs: any[] | null, record: any) =>
        Array.isArray(docs) && docs.length > 0 ? (
          <div className="d-flex flex-column align-items-center gap-1">
            {docs.map((doc) => (
              <div
                key={doc.document_id}
                className="d-flex align-items-center gap-2"
              >
                <span
                  onClick={() => handleDownloadDrugReport(doc, record)}
                  className="wip-chip wip-chip-success"
                  style={{ cursor: "pointer" }}
                >
                  {doc.original_name ?? doc.file_name}{" "}
                  <i className="ti ti-download" />
                </span>
              </div>
            ))}
          </div>
        ) : (
          "-"
        ),
    },
    {
      title: "View Log",
      key: "viewLog",
      onCell: () => cellWidth(110),
      render: (_: any, record: any) => (
        <div
          className="text-center text-dark"
          style={{ cursor: "pointer" }}
          onClick={() => handleViewLog(record.case_id)}
        >
          <i className="ti ti-eye"></i>
        </div>
      ),
    },
  ];

  const handleDownloadExcel = () => {
    exportAntdTableToExcel({
      columns,
      dataSource: filteredData,
      fileName: "Work_In_Progress_Report",
      sheetName: "Work In Progress",
    });
  };

  const loading = liveCaseProgress.loading;

  return (
    <div className="px-2">
      <div className="card shadow-sm border-0">
        <div className="card-body">
          <SearchFilter
            ref={filterRef}
            value={searchData}
            onChange={handleFilterChange}
            storageKey="liveCaseProgress"
            errors={errors}
          />

          {/* Buttons */}

          <div className="d-flex justify-content-end gap-2 mt-2">
            <button
              className="btn btn-primary px-4"
              onClick={handleSearch}
              // disabled={searchCaseAPI.loading}
            >
              <i className="ti ti-search me-1"></i>
              Search
              {/* {liveCaseProgress.loading ? "Searching..." : "Search"} */}
            </button>

            <button
              className="btn btn-outline-secondary px-4"
              onClick={handleClear}
            >
              <i className="ti ti-refresh me-1"></i>
              Clear Filter
            </button>
          </div>
        </div>
      </div>

      {/* Table  */}
      <div className="card mt-3">
        {liveCaseProgress.loading ? (
          <Loading />
        ) : (
          <>
            <div className="d-flex justify-content-end pe-2 py-2 pb-0">
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={handleDownloadExcel}
                disabled={loading}
              >
                <i className="ti ti-download" /> Export to Excel
              </button>
            </div>
            <Datatable
              dataSource={filteredData}
              columns={columns}
              Selection={false}
              fixedHeader
              tableHeight={650}
              pagination={pagination}
              handleTableChange={handleTableChange}
            />
          </>
        )}
      </div>

      {/* Timeline Modal */}
      {showTimeline && (
        <TimelineModal
          timelineData={timelineData}
          loading={timelineLoading}
          onClose={() => {
            setShowTimeline(false);
            setTimelineData([]);
          }}
        />
      )}
    </div>
  );
};

export default WorkInProgress;

// import { useEffect, useRef, useState } from "react";
// import { ColumnsType } from "antd/es/table";
// import { cellWidth } from "../../../core/methods/tableCellWidth";
// import { useNavigate } from "react-router";
// import { all_routes } from "../../router/all_routes";
// import Datatable from "../../../core/common/dataTable/index";
// import TimelineModal from "../../../core/common/TimelineModal";

// import {
//   parseNumber,
//   safeLocaleCompare,
// } from "../../../core/methods/sorterMethods";
// import {
//   SearchFilterErrors,
//   SearchFilterRef,
//   SearchPayload,
// } from "../../../core/common/ManagerSingleSearchFilter/types";
// import useApi from "../../../core/customhook/useApi";
// import SearchFilter from "../../../core/common/ManagerSingleSearchFilter/SearchFilter";
// import { formatDate } from "../../../core/common/TabelValue";
// import { PaginationType } from "../../../types/common-type";
// import Loading from "../../../core/common/custom-loading/Loading";
// import webservice_url from "../../../core/webservice/Webservice_url";
// import { Switch } from "antd";
// import { getSearchableHeader } from "../../../core/utils/getSearchableHeader";

// export interface CaseTableRecord {
//   key: string;
//   sno: number;
//   applicant_name: string;
//   case_id: string;
//   case_spoc: string;
//   employee_id: string;
//   case_received_date: string;
//   sla_start_date: string;
//   due_date: string;
//   total_checks: number;
//   work_in_progress: number;
//   completed: number;
//   stop: number;
//   insuff_expire: string;
//   actions: string;
// }

// export const actionOptions = [
//   { value: "stop_case", label: "Stop Case" },
//   { value: "stop_check", label: "Stop Check" },
//   { value: "add_check", label: "Add Check" },
//   { value: "generate_report", label: "Generate Report" },
// ];

// const initialSearchData: SearchPayload = {
//   org_ids: [],
//   project_ids: [],
//   location_name: [],
//   program_ids: [],
//   department_ids: [],
// };

// type ReportVisibility = {
//   assisted: boolean;
//   nonAssisted: boolean;
// };

// const WorkInProgress = ({ status_id }: { status_id: string }) => {
//   const navigate = useNavigate();
//   const [itemList, setItemList] = useState([]);
//   const [timelineData, setTimelineData] = useState<any[]>([]);
//   const [timelineLoading, setTimelineLoading] = useState<boolean>(false);
//   const [showTimeline, setShowTimeline] = useState<boolean>(false);
//   const filterRef = useRef<SearchFilterRef>(null);
//   const [searchData, setSearchData] =
//     useState<SearchPayload>(initialSearchData);
//   const [inventoryColumnSearch, setInventoryColumnSearch] = useState<
//     Record<string, string>
//   >({});
//   const [pagination, setPagination] = useState<PaginationType>({
//     current: 1,
//     pageSize: 1000,
//     total: 1,
//   });
//   const [reportVisibility, setReportVisibility] = useState<
//     Record<string, ReportVisibility>
//   >({});

//   type InviteItem = any;
//   const filteredData = (Array.isArray(itemList) ? itemList : []).filter(
//     (item: InviteItem) => {
//       return Object.entries(inventoryColumnSearch).every(([key, value]) => {
//         if (!value) return true;

//         return String(item[key as keyof InviteItem] ?? "")
//           .toLowerCase()
//           .includes(value.toLowerCase());
//       });
//     },
//   );

//   const liveCaseProgress = useApi<any>(
//     "/api/v1/agency/manager-live-case-progress",
//     {
//       method: "POST",
//       autoCall: false,
//       handleGlobalError: false,
//     },
//   );
//   const showReportAPI = useApi<any>(webservice_url.upload_assistedReports, {
//     method: "POST",
//     autoCall: false,
//     handleGlobalError: false,
//   });

//   const getCaseLog = useApi<any>(webservice_url.ViewLogAPI, {
//     method: "POST",
//     autoCall: false,
//     handleGlobalError: false,
//   });

//   const [errors, setErrors] = useState<SearchFilterErrors>({});
//   const handleViewLog = async (caseId?: string) => {
//     if (!caseId) return;
//     setShowTimeline(true);
//     setTimelineLoading(true);
//     try {
//       const res = await getCaseLog.callApi({ case_id: caseId });
//       setTimelineData(res?.data ?? []);
//     } catch (err) {
//       console.error(err);
//       setTimelineData([]);
//     } finally {
//       setTimelineLoading(false);
//     }
//   };

//   const handleSearch = async () => {
//     try {
//       const payload = {
//         status: status_id,
//         ...searchData,
//         page: pagination?.current ?? 1,
//         limit: pagination?.pageSize ?? 10,
//       };

//       const res = await liveCaseProgress.callApi(payload);

//       if (!res?.success) {
//         setItemList([]);
//         setReportVisibility({});
//         return;
//       }

//       const rows = res.data?.rows ?? [];

//       setItemList(rows);

//       // Set Assisted / Non-Assisted switch values from API
//       const visibility: Record<string, ReportVisibility> = {};

//       rows.forEach((item: any) => {
//         const assignmentId = String(item?.assignment_id ?? "");

//         if (assignmentId) {
//           visibility[assignmentId] = {
//             assisted: Boolean(item?.show_assisted_report),
//             nonAssisted: Boolean(item?.show_nonassisted_report),
//           };
//         }
//       });

//       setReportVisibility(visibility);

//       setPagination((prev) => ({
//         ...prev,
//         total: res?.data?.pagination?.totalRecords || 0,
//       }));
//     } catch (err) {
//       console.error(err);
//     }
//   };

//   useEffect(() => {
//     if (!status_id) return;
//     handleSearch();
//   }, [status_id, pagination?.current, pagination?.pageSize]);

//   const handleClear = () => {
//     filterRef.current?.clear();
//   };

//   const handleTableChange = (pgConfig: any) => {
//     if (
//       pgConfig.current !== pagination.current ||
//       pgConfig.pageSize !== pagination.pageSize
//     ) {
//       setPagination((prev) => ({
//         ...prev,
//         current: pgConfig.current,
//         pageSize: pgConfig.pageSize,
//       }));
//     }
//   };

//   const DownloadAssestedReport = useApi<any>("", {
//     method: "GET",
//     autoCall: false,
//     handleGlobalError: false,
//     responseType: "blob",
//   });
//   const handleDownloadReport = async (
//     assignmentId: any,
//     reportType: "assisted" | "non-assisted",
//   ) => {
//     const assignmentID = assignmentId?.assignment_id;

//     try {
//       const response: any = await DownloadAssestedReport.callApi(
//         undefined,
//         `${webservice_url.DownloadAssestedReport}/${assignmentID}/${reportType}-report/download-pdf`,
//       );

//       const blob = new Blob([response], {
//         type: "application/pdf",
//       });

//       const url = window.URL.createObjectURL(blob);

//       const link = document.createElement("a");
//       link.href = url;
//       link.download = `${reportType}_Report_${assignmentID}.pdf`;

//       document.body.appendChild(link);
//       link.click();

//       document.body.removeChild(link);
//       window.URL.revokeObjectURL(url);
//     } catch (error) {
//       console.log(`${reportType} Report Download Error:`, error);
//     }
//   };
//   const handleReportVisibility = async (
//     record: any,
//     type: "assisted" | "non-assisted",
//     checked: boolean,
//   ) => {
//     const assignmentId = record?.assignment_id;

//     if (!assignmentId) return;

//     const id = String(assignmentId);

//     const current = reportVisibility[id] || {
//       assisted: false,
//       nonAssisted: false,
//     };

//     const payload = [
//       {
//         assignment_id: assignmentId,
//         show_assisted_report: type === "assisted" ? checked : current.assisted,

//         show_nonassisted_report:
//           type === "non-assisted" ? checked : current.nonAssisted,
//       },
//     ];

//     try {
//       console.log("Show Report Payload:", payload);

//       await showReportAPI.callApi(payload);

//       setReportVisibility((prev) => ({
//         ...prev,
//         [id]: {
//           assisted: type === "assisted" ? checked : current.assisted,

//           nonAssisted: type === "non-assisted" ? checked : current.nonAssisted,
//         },
//       }));
//     } catch (error) {
//       console.error("Show Report Update Error:", error);
//     }
//   };
//   const columns: ColumnsType<any> = [
//     {
//       title: "S.No",
//       key: "sno",
//       align: "center",
//       width: 80,
//       render: (_: any, __: any, index: number) => index + 1,
//     },
//     {
//       title: getSearchableHeader(
//         "Applicant Name",
//         "applicant_name",
//         setInventoryColumnSearch,
//       ),
//       dataIndex: "applicant_name",
//       key: "applicant_name",
//       onCell: () => cellWidth(190),
//       render: (text, record: any) => (
//         <div
//           className="wip-link-cell"
//           onClick={() => {
//             navigate(all_routes.manager_caseDetails, {
//               state: {
//                 case_id: record.case_id,
//                 assignment_id: record.assignment_id,
//               },
//             });
//           }}
//         >
//           {text}
//         </div>
//       ),
//       sorter: (a, b) => safeLocaleCompare(a, b, "applicant_name"),
//     },
//     {
//       title: getSearchableHeader(
//         "Case Id",
//         "case_id",
//         setInventoryColumnSearch,
//       ),
//       dataIndex: "case_id",
//       key: "case_id",
//       onCell: () => cellWidth(190),
//       sorter: (a, b) => safeLocaleCompare(a, b, "case_id"),
//     },
//     {
//       title: getSearchableHeader(
//         "Project",
//         "project_name",
//         setInventoryColumnSearch,
//       ),
//       dataIndex: "project_name",
//       key: "project_name",
//       onCell: () => cellWidth(220),
//       width: "220px",
//       sorter: (a: any, b: any) =>
//         (a.project_name ?? "").localeCompare(b.project_name ?? ""),
//     },
//     {
//       title: getSearchableHeader(
//         "Program",
//         "program_name",
//         setInventoryColumnSearch,
//       ),
//       dataIndex: "program_name",
//       key: "program_name",
//       onCell: () => cellWidth(220),
//       width: "220px",
//       sorter: (a: any, b: any) =>
//         (a.program_name ?? "").localeCompare(b.program_name ?? ""),
//     },
//     {
//       title: getSearchableHeader(
//         "Department",
//         "department_name",
//         setInventoryColumnSearch,
//       ),
//       dataIndex: "department_name",
//       key: "department_name",
//       onCell: () => cellWidth(220),
//       width: "220px",
//       sorter: (a: any, b: any) =>
//         (a.department_name ?? "").localeCompare(b.department_name ?? ""),
//     },
//     {
//       title: getSearchableHeader(
//         "Case Spoc",
//         "case_spoc",
//         setInventoryColumnSearch,
//       ),
//       dataIndex: "case_spoc",
//       key: "case_spoc",
//       onCell: () => cellWidth(140),
//       sorter: (a, b) => safeLocaleCompare(a, b, "case_spoc"),
//     },
//     {
//       title: getSearchableHeader(
//         "Candidate Id",
//         "hrms_id",
//         setInventoryColumnSearch,
//       ),
//       dataIndex: "hrms_id",
//       key: "hrms_id",
//       onCell: () => cellWidth(160),
//       sorter: (a, b) => safeLocaleCompare(a, b, "hrms_id"),
//     },
//     {
//       title: getSearchableHeader(
//         "Case Received Date",
//         "case_received_date",
//         setInventoryColumnSearch,
//       ),
//       dataIndex: "case_received_date",
//       key: "case_received_date",
//       onCell: () => cellWidth(200),
//       render: (value: string) => formatDate(value),
//     },
//     {
//       title: getSearchableHeader(
//         "SLA Start Date",
//         "sla_start_date",
//         setInventoryColumnSearch,
//       ),
//       dataIndex: "sla_start_date",
//       key: "sla_start_date",
//       onCell: () => cellWidth(190),
//       render: (value: string) => formatDate(value),
//     },
//     {
//       title: getSearchableHeader(
//         "Due Date",
//         "due_date",
//         setInventoryColumnSearch,
//       ),
//       dataIndex: "due_date",
//       key: "due_date",
//       onCell: () => cellWidth(140),
//       render: (value: string) => formatDate(value),
//     },

//     {
//       title: getSearchableHeader(
//         "Total Checks",
//         "total_checks",
//         setInventoryColumnSearch,
//       ),
//       dataIndex: "total_checks",
//       key: "total_checks",
//       align: "center",
//       onCell: () => cellWidth(160),
//       sorter: (a: any, b: any) =>
//         parseNumber(a.total_checks) - parseNumber(b.total_checks),
//     },
//     {
//       title: "Assisted",
//       dataIndex: "assisted_checks",
//       key: "assisted_checks",
//       onCell: () => cellWidth(200),
//       align: "center",
//       sorter: (a, b) => Number(a.assisted_checks) - Number(b.assisted_checks),
//       render: (_value: number, record: any) => {
//         const verified = Number(record.assisted_verified_checks ?? 0);
//         const total = Number(record.assisted_checks ?? 0);
//         const isComplete = verified === total;

//         return (
//           <div>
//             <span className="wip-chip wip-chip-progress">
//               {verified} / {total}
//             </span>

//             <div
//               className={`mt-1 ${isComplete ? "text-success" : "text-warning"}`}
//             >
//               {isComplete ? "Complete" : "In Progress"}
//             </div>
//           </div>
//         );
//       },
//     },
//     {
//       title: "Non-Assisted",
//       dataIndex: "non_assisted_checks",
//       key: "non_assisted_checks",
//       onCell: () => cellWidth(200),
//       align: "center",
//       sorter: (a, b) =>
//         Number(a.non_assisted_checks) - Number(b.non_assisted_checks),
//       render: (_value: number, record: any) => {
//         const verified = Number(record.non_assisted_verified_checks ?? 0);
//         const total = Number(record.non_assisted_checks ?? 0);
//         const isComplete = verified === total;

//         return (
//           <div>
//             <span className="wip-chip wip-chip-progress">
//               {verified} / {total}
//             </span>

//             <div
//               className={`mt-1 ${isComplete ? "text-success" : "text-warning"}`}
//             >
//               {isComplete ? "Complete" : "In Progress"}
//             </div>
//           </div>
//         );
//       },
//     },
//     {
//       title: getSearchableHeader(
//         "Candidate Submited",
//         "candidate_submit",
//         setInventoryColumnSearch,
//       ),
//       dataIndex: "candidate_submit",
//       key: "candidate_submit",
//       onCell: () => cellWidth(200),
//       align: "center",
//       sorter: (a, b) => a.candidate_submit - b.candidate_submit,
//       render: (value: number) => (
//         <span className="wip-chip wip-chip-success">{value ?? 0}</span>
//       ),
//     },
//     {
//       title: getSearchableHeader(
//         "Work In Progress",
//         "work_in_progress",
//         setInventoryColumnSearch,
//       ),
//       dataIndex: "work_in_progress",
//       key: "work_in_progress",
//       align: "center",
//       sorter: (a: any, b: any) =>
//         parseNumber(a.work_in_progress) - parseNumber(b.work_in_progress),
//     },
//     {
//       title: getSearchableHeader(
//         "Completed",
//         "completed",
//         setInventoryColumnSearch,
//       ),
//       dataIndex: "completed",
//       key: "completed",
//       align: "center",
//       sorter: (a: any, b: any) =>
//         parseNumber(a.completed) - parseNumber(b.completed),
//     },
//     {
//       title: getSearchableHeader("Stop", "stopped", setInventoryColumnSearch),
//       dataIndex: "stopped",
//       key: "stopped",
//       align: "center",
//       sorter: (a: any, b: any) =>
//         parseNumber(a.stopped) - parseNumber(b.stopped),
//     },
//     // {
//     //   title: "Insuff Expire",
//     //   dataIndex: "insufficiency_expired",
//     //   key: "insufficiency_expired",
//     //   onCell: () => cellWidth(150),
//     //   align: "center",
//     //   sorter: (a: any, b: any) =>
//     //     parseNumber(a.insufficiency_expired) -
//     //     parseNumber(b.insufficiency_expired),
//     // },
//     {
//       title: "Show Assisted Report",
//       dataIndex: "",
//       key: "show_assisted_report",
//       onCell: () => cellWidth(180),
//       align: "center",
//       render: (_: any, record: any) => {
//         const assignmentId = String(record?.assignment_id ?? "");

//         const isChecked = reportVisibility[assignmentId]?.assisted ?? false;

//         return (
//           <Switch
//             checked={isChecked}
//             loading={showReportAPI.loading}
//             onChange={(checked) =>
//               handleReportVisibility(record, "assisted", checked)
//             }
//           />
//         );
//       },
//     },
//     {
//       title: "Show Non-assisted Report",
//       dataIndex: "",
//       key: "show_nonassisted_report",
//       onCell: () => cellWidth(200),
//       align: "center",
//       render: (_: any, record: any) => {
//         const assignmentId = String(record?.assignment_id ?? "");

//         const isChecked = reportVisibility[assignmentId]?.nonAssisted ?? false;

//         return (
//           <Switch
//             checked={isChecked}
//             loading={showReportAPI.loading}
//             onChange={(checked) =>
//               handleReportVisibility(record, "non-assisted", checked)
//             }
//           />
//         );
//       },
//     },
//     {
//       title: "Assisted Report",
//       dataIndex: "",
//       onCell: () => cellWidth(190),
//       render: (_: any, record: any) => (
//         <button
//           className="btn btn-sm btn-primary d-flex gap-2"
//           onClick={() => handleDownloadReport(record, "assisted")}
//           // disabled={loading}
//         >
//           <i className="bi bi-download"></i>
//           <span className="wip-btn-label">Download</span>
//         </button>
//       ),
//     },
//     {
//       title: "Non-assisted Report",
//       dataIndex: "",
//       onCell: () => cellWidth(180),
//       render: (_: any, record: any) => (
//         <button
//           className="btn btn-sm btn-primary d-flex gap-2"
//           onClick={() => handleDownloadReport(record, "non-assisted")}
//           // disabled={loading}
//         >
//           <i className="bi bi-download"></i>
//           <span className="wip-btn-label">Download</span>
//         </button>
//       ),
//     },

//     {
//       title: "View Log",
//       key: "viewLog",
//       onCell: () => cellWidth(190),
//       render: (_: any, record: any) => (
//         <div
//           className="text-center text-dark"
//           style={{ cursor: "pointer" }}
//           onClick={() => handleViewLog(record.case_id)}
//         >
//           <i className="ti ti-eye"></i>
//         </div>
//       ),
//     },
//   ];

//   return (
//     <div className="px-2">
//       <div className="card shadow-sm border-0">
//         <div className="card-body">
//           <SearchFilter
//             ref={filterRef}
//             value={searchData}
//             onChange={setSearchData}
//             storageKey="liveCaseProgress"
//           />

//           {/* Buttons */}

//           <div className="d-flex justify-content-end gap-2 mt-2">
//             <button
//               className="btn btn-primary px-4"
//               onClick={handleSearch}
//               // disabled={searchCaseAPI.loading}
//             >
//               <i className="ti ti-search me-1"></i>
//               Search
//               {/* {liveCaseProgress.loading ? "Searching..." : "Search"} */}
//             </button>

//             <button
//               className="btn btn-outline-secondary px-4"
//               onClick={handleClear}
//             >
//               <i className="ti ti-refresh me-1"></i>
//               Clear
//             </button>
//           </div>
//         </div>
//       </div>

//       {/* Table  */}
//       <div className="card mt-3">
//         {liveCaseProgress.loading ? (
//           <Loading />
//         ) : (
//           <Datatable
//             dataSource={filteredData}
//             columns={columns}
//             Selection={false}
//             pagination={pagination}
//             handleTableChange={handleTableChange}
//           />
//         )}
//       </div>

//       {/* Timeline Modal */}
//       {showTimeline && (
//         <TimelineModal
//           timelineData={timelineData}
//           loading={timelineLoading}
//           onClose={() => {
//             setShowTimeline(false);
//             setTimelineData([]);
//           }}
//         />
//       )}
//     </div>
//   );
// };

// export default WorkInProgress;
